import { FunctionComponent, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoIcon from '@mui/icons-material/Info';
import { claimsApi, ApiError } from '../../../services/api';
import styles from './DisputeClaimScreen.module.css';

interface DisputeReason {
  id: string;
  label: string;
  description: string;
}

const disputeReasons: DisputeReason[] = [
  {
    id: 'incorrect-amount',
    label: 'Incorrect charge amount',
    description: 'The amount charged differs from what was quoted or is unreasonably high'
  },
  {
    id: 'already-paid',
    label: 'Already paid',
    description: 'This bill has already been paid in full or partially'
  },
  {
    id: 'wrong-patient',
    label: 'Wrong patient',
    description: 'This claim is not for services I received'
  },
  {
    id: 'service-not-received',
    label: 'Service not received',
    description: 'I did not receive some or all of the services listed'
  },
  {
    id: 'insurance-should-cover',
    label: 'Insurance should cover',
    description: 'My insurance should have covered this service'
  },
  {
    id: 'billing-error',
    label: 'Billing error',
    description: 'There are errors in how the service was coded or billed'
  },
  {
    id: 'duplicate-charge',
    label: 'Duplicate charge',
    description: 'I was charged multiple times for the same service'
  },
  {
    id: 'other',
    label: 'Other',
    description: 'My issue is not listed above'
  }
];

const DisputeClaimScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const { claimId } = useParams();
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [detailedExplanation, setDetailedExplanation] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [contactPreference, setContactPreference] = useState<'email' | 'phone' | 'both'>('email');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleReasonToggle = (reasonId: string) => {
    setSelectedReasons(prev => 
      prev.includes(reasonId) 
        ? prev.filter(id => id !== reasonId)
        : [...prev, reasonId]
    );
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setAttachments(prev => [...prev, ...newFiles]);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (selectedReasons.length === 0 || !detailedExplanation.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (!claimId) {
        throw new Error('Claim ID is required');
      }

      // For now, we'll send the data as JSON. In a real implementation,
      // you might need to handle file uploads separately or modify the API service
      // to support FormData for file uploads
      const disputeData = {
        reasons: selectedReasons,
        explanation: detailedExplanation,
        contactPreference,
        // Note: File attachments would need special handling in a real implementation
        attachmentCount: attachments.length
      };

      await claimsApi.dispute(claimId, disputeData);
      
      setShowSuccess(true);
      setTimeout(() => {
        navigate('/claims');
      }, 3000);
    } catch (error) {
      console.error('Error submitting dispute:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className={styles.successScreen}>
        <div className={styles.successContent}>
          <div className={styles.successIcon}>
            <CheckCircleIcon />
          </div>
          <h1 className={styles.successTitle}>Dispute Submitted Successfully</h1>
          <p className={styles.successMessage}>
            We've received your dispute and are already working on it. Our team will review your case 
            and reach out to you within 24-48 hours.
          </p>
          <div className={styles.successDetails}>
            <h3>What happens next?</h3>
            <ul>
              <li>Our dispute specialists will review your case</li>
              <li>We'll contact your insurance and healthcare provider</li>
              <li>You'll receive updates via your preferred contact method</li>
              <li>We'll fight to get this resolved in your favor</li>
            </ul>
          </div>
          <p className={styles.redirectMessage}>
            Redirecting you back to claims...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.disputeClaimScreen}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <h1 className={styles.headerTitle}>Dispute Claim</h1>
        <GavelIcon className={styles.headerIcon} />
      </div>

      <div className={styles.content}>
        <div className={styles.intro}>
          <h2>We're here to help you dispute this claim</h2>
          <p>Tell us what's wrong and we'll work with your insurance and provider to resolve it.</p>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            Why are you disputing this claim?
            <span className={styles.required}>*</span>
          </h3>
          <p className={styles.sectionDescription}>Select all that apply</p>
          
          <div className={styles.reasonsList}>
            {disputeReasons.map(reason => (
              <div 
                key={reason.id} 
                className={`${styles.reasonItem} ${
                  selectedReasons.includes(reason.id) ? styles.selected : ''
                }`}
                onClick={() => handleReasonToggle(reason.id)}
              >
                <div className={styles.reasonCheckbox}>
                  {selectedReasons.includes(reason.id) && <CheckCircleIcon />}
                </div>
                <div className={styles.reasonContent}>
                  <h4 className={styles.reasonLabel}>{reason.label}</h4>
                  <p className={styles.reasonDescription}>{reason.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            Provide detailed explanation
            <span className={styles.required}>*</span>
          </h3>
          <p className={styles.sectionDescription}>
            The more details you provide, the better we can help resolve your dispute
          </p>
          
          <textarea
            className={styles.explanationTextarea}
            value={detailedExplanation}
            onChange={(e) => setDetailedExplanation(e.target.value)}
            placeholder="Explain what happened, when it happened, and any relevant details about your claim..."
            rows={8}
          />
          
          <div className={styles.textareaHint}>
            {detailedExplanation.length}/1000 characters
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Supporting documents</h3>
          <p className={styles.sectionDescription}>
            Upload any receipts, EOBs, or other documents that support your dispute
          </p>
          
          <div className={styles.uploadArea}>
            <input
              type="file"
              id="file-upload"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={handleFileUpload}
              className={styles.fileInput}
            />
            <label htmlFor="file-upload" className={styles.uploadButton}>
              <AttachFileIcon />
              <span>Attach files</span>
            </label>
            
            <p className={styles.uploadHint}>
              PDF, JPG, PNG, DOC up to 10MB each
            </p>
          </div>

          {attachments.length > 0 && (
            <div className={styles.attachmentsList}>
              {attachments.map((file, index) => (
                <div key={index} className={styles.attachmentItem}>
                  <AttachFileIcon className={styles.attachmentIcon} />
                  <span className={styles.attachmentName}>{file.name}</span>
                  <button
                    className={styles.removeButton}
                    onClick={() => handleRemoveAttachment(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>How should we contact you?</h3>
          
          <div className={styles.contactOptions}>
            <label className={`${styles.contactOption} ${
              contactPreference === 'email' ? styles.selected : ''
            }`}>
              <input
                type="radio"
                name="contact"
                value="email"
                checked={contactPreference === 'email'}
                onChange={(e) => setContactPreference(e.target.value as any)}
              />
              <span>Email</span>
            </label>
            
            <label className={`${styles.contactOption} ${
              contactPreference === 'phone' ? styles.selected : ''
            }`}>
              <input
                type="radio"
                name="contact"
                value="phone"
                checked={contactPreference === 'phone'}
                onChange={(e) => setContactPreference(e.target.value as any)}
              />
              <span>Phone</span>
            </label>
            
            <label className={`${styles.contactOption} ${
              contactPreference === 'both' ? styles.selected : ''
            }`}>
              <input
                type="radio"
                name="contact"
                value="both"
                checked={contactPreference === 'both'}
                onChange={(e) => setContactPreference(e.target.value as any)}
              />
              <span>Both</span>
            </label>
          </div>
        </div>

        <div className={styles.infoBox}>
          <InfoIcon className={styles.infoIcon} />
          <div>
            <h4>What happens after you submit?</h4>
            <p>
              Our dispute resolution team will review your case and contact your insurance 
              company and healthcare provider on your behalf. We'll keep you updated throughout 
              the process and fight to get the best outcome for you.
            </p>
          </div>
        </div>

        <div className={styles.actions}>
          <button 
            className={styles.cancelButton}
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
          
          <button 
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={
              isSubmitting || 
              selectedReasons.length === 0 || 
              !detailedExplanation.trim()
            }
          >
            {isSubmitting ? 'Submitting...' : 'Submit Dispute'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisputeClaimScreen;